import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useIsDemoAccount } from '../../../accountSelector/useIsDemoAccount';
import { postsMessages } from './postsMessages';
import { PostsQuotaLine } from './PostsQuotaLine';

interface PostsHeadActionsProps {
  accountId: number;
  /** "New post". Default: the empty Composer. The Calendar opens it on the day in view. */
  onNewPost?: () => void;
}

/**
 * The right side of the Posts head on both tabs (design navigation-v2 c 3b, 3c): the scheduled-post
 * quota, "Make in Studio" and "New post". The demo artist is read-only: no buttons.
 */
const PostsHeadActionsComponent: React.FC<PostsHeadActionsProps> = ({ accountId, onNewPost }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const isDemo = useIsDemoAccount();
  const newPost = useCallback(() => {
    if (onNewPost !== undefined) {
      onNewPost();
      return;
    }
    router.push(SMLRoutes.promoteComposer);
  }, [onNewPost, router]);

  return (
    <div className="d-flex flex-wrap align-items-center gap-2">
      <PostsQuotaLine accountId={accountId} />
      {!isDemo && (
        <>
          <SMLButton color="secondary" outline size="sm" omitCap onClick={() => router.push(SMLRoutes.promoteStudio)}>
            <FontAwesomeIcon icon={faSparkles} className="me-1" />
            {intl.formatMessage(postsMessages.makeInStudio)}
          </SMLButton>
          <SMLButton color="primary" size="sm" omitCap onClick={newPost} testID="posts-new-post">
            <FontAwesomeIcon icon={faPlus} className="me-1" />
            {intl.formatMessage(postsMessages.newPost)}
          </SMLButton>
        </>
      )}
    </div>
  );
};

export const PostsHeadActions = TypedMemo(PostsHeadActionsComponent);
